/**
 * workoutSave — Orchestrates the full workout save pipeline:
 *   1. Build payload & insert workout row
 *   2. Insert strength_events
 *   3. Auto-submit to active challenges
 *   4. Recompute leaderboard + strength score
 *   5. Check milestones & badges
 *   6. Create PR posts + workout summary post
 *
 * The record screen calls `saveWorkout()` and handles the result.
 */
import { Alert, Platform } from 'react-native';
import { supabase } from '@/lib/supabase';
import { normalizeToV1Lift, calculateE1RM } from '@/lib/lifts/canonical';
import { createLogger } from '@/lib/logger';
import { uploadVideo } from '@/lib/uploadVideo';
import { enqueueScoreJob } from '@/lib/scoreQueue';
import { checkMilestones, type MilestoneDef } from '@/lib/milestones';
import { hapticsUtil } from '@/lib/haptics';
import { insertStrengthEvents, type WorkoutPayload } from '@/lib/strengthEvents';
import { autoSubmitChallenges } from '@/lib/challengeAutoSubmit';
import type { ExerciseEntry } from '@/components/record';
import { trackEvent } from '@/lib/analytics';
import { captureError } from '@/lib/errorReporting';
import { offlineQueue } from '@/lib/offlineQueue';
import { setSyncStatus } from '@/lib/offlineStatus';
import { notifyOpponentLoggedLift } from '@/lib/notificationTriggers';
import { validateWorkout, type LiftEntry, type PriorLift } from '@/lib/antiGaming';
import { evaluateBadges } from '@/lib/badges';
import { checkRateLimit } from '@/lib/rateLimiter';
import { getSignedHeaders } from '@/lib/requestSigning';
import { clearBaselineCache } from '@/lib/baselineClient';

const log = createLogger('WorkoutSave');

// Idempotency lock — prevents concurrent saves from the same session
let isSaving = false;
const SAVE_COOLDOWN_MS = 3000; // 3 seconds minimum between saves
let lastSaveAt = 0;

export interface SaveWorkoutProfile {
  id: string;
  units?: string;
  bodyweight?: number;
  home_gym_id?: string | null;
  gym_id?: string | null;
}

export interface SaveWorkoutOptions {
  workoutDate: Date;
  workoutName: string;
  sessionNotes: string;
  supersetGroups: Record<string, string[]>;
  authUserId: string;
  refreshProfile?: () => Promise<any>;
}

export interface SaveWorkoutResult {
  workoutId: string;
  newMilestones: MilestoneDef[];
  badgesUnlocked: any[];
}

export async function saveWorkout(
  entries: ExerciseEntry[],
  profile: SaveWorkoutProfile,
  options: SaveWorkoutOptions,
): Promise<SaveWorkoutResult> {
  // Prevent concurrent/duplicate saves
  const now = Date.now();
  if (isSaving || (now - lastSaveAt < SAVE_COOLDOWN_MS)) {
    log.warn('[WorkoutSave] Duplicate save blocked', { isSaving, timeSinceLastSave: now - lastSaveAt });
    throw new Error('Save already in progress. Please wait a moment.');
  }
  isSaving = true;
  lastSaveAt = now;

  try {

  // Rate limit check
  const rateCheck = await checkRateLimit('workout_save');
  if (!rateCheck.allowed) {
    isSaving = false;
    throw new Error(`Too many saves. Try again in ${Math.ceil(rateCheck.resetIn / 60000)} minutes.`);
  }

  const {
    workoutDate,
    workoutName,
    sessionNotes,
    supersetGroups,
    authUserId,
    refreshProfile,
  } = options;

  // Validate workout date — reject future dates and dates too far in the past
  const nowDate = new Date();
  const maxPastMs = 365 * 24 * 60 * 60 * 1000; // 1 year

  if (workoutDate > nowDate) {
    throw new Error('Workout date cannot be in the future.');
  }
  if (nowDate.getTime() - workoutDate.getTime() > maxPastMs) {
    throw new Error('Workout date cannot be more than 1 year in the past.');
  }

  // --- Fetch engagement preferences ---
  const { data: engagementPrefs } = await supabase
    .from('engagement_preferences')
    .select('auto_share_workouts')
    .eq('user_id', profile.id)
    .maybeSingle();

  const shouldAutoShare = engagementPrefs?.auto_share_workouts ?? false; // default OFF — user must opt in

  // --- Build workout payload ---
  const effectiveWorkoutName =
    workoutName.trim() ||
    `Workout — ${workoutDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const workoutPayload: WorkoutPayload = {
    exercises: [],
    units: profile.units ?? 'lbs',
    workoutName: effectiveWorkoutName,
    sessionNotes: sessionNotes.trim() || null,
    supersetGroups: Object.keys(supersetGroups).length > 0 ? supersetGroups : null,
  };

  for (const entry of entries) {
    const weight = parseFloat(entry.weight) || 0;
    const reps = parseInt(entry.reps) || 0;
    const sets = parseInt(entry.sets) || 1;

    let uploadedVideoUrl: string | null = null;
    if (entry.videoUri) {
      log.info('Uploading video for', entry.exercise!.name);
      try {
        uploadedVideoUrl = await uploadVideo(entry.videoUri, profile.id);
        log.info('Video uploaded successfully:', uploadedVideoUrl);
      } catch (uploadError: any) {
        log.error('Video upload failed (non-blocking):', uploadError);
        // Video is optional metadata — never block workout save
        uploadedVideoUrl = null;
      }
    }

    const exerciseSets = [];
    if (entry.setDetails && entry.setDetails.length > 0) {
      for (let i = 0; i < entry.setDetails.length; i++) {
        const sd = entry.setDetails[i];
        exerciseSets.push({
          reps: parseInt(sd.reps) || 0,
          weight: parseFloat(sd.weight) || 0,
          is_pr: entry.isPR && i === 0,
          media_url: uploadedVideoUrl,
        });
      }
    } else {
      for (let i = 0; i < sets; i++) {
        exerciseSets.push({
          reps,
          weight,
          is_pr: entry.isPR && i === 0,
          media_url: uploadedVideoUrl,
        });
      }
    }

    workoutPayload.exercises.push({
      exercise_id: entry.exercise!.id,
      name: entry.exercise!.name,
      sets: exerciseSets,
      notes: entry.notes || null,
      rpe: entry.rpe ? parseInt(entry.rpe, 10) : null,
      equipment: entry.equipment || null,
      supersetGroupId: entry.supersetGroupId || null,
      is_competition: false,
    });
  }

  // Guard: don't save empty workouts
  if (workoutPayload.exercises.length === 0) {
    throw new Error('Add at least one exercise before saving.');
  }

  log.info('Creating workout record with', workoutPayload.exercises.length, 'exercises');

  // --- Anti-gaming integrity check ---
  try {
    const weightUnit = String(profile.units ?? 'lbs').toLowerCase() === 'kg' ? 'kg' : 'lb';
    const bodyweightLbs = profile.bodyweight
      ? (weightUnit === 'kg' ? (profile.bodyweight as number) * 2.20462 : (profile.bodyweight as number))
      : undefined;

    const liftEntries: LiftEntry[] = [];
    for (const ex of workoutPayload.exercises) {
      for (const s of ex.sets) {
        if (s.weight > 0 && s.reps > 0) {
          liftEntries.push({
            liftKey: ex.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            weight: s.weight,
            weightUnit,
            reps: s.reps,
            bodyweightLbs,
          });
        }
      }
    }

    // Fetch recent priors for velocity check
    const liftKeys = [...new Set(liftEntries.map(e => e.liftKey))];
    const priorLiftsByKey: Record<string, PriorLift[]> = {};
    if (liftKeys.length > 0) {
      const { data: priors } = await supabase
        .from('strength_events')
        .select('lift_key, weight, weight_unit, performed_at')
        .eq('profile_id', profile.id)
        .in('lift_key', liftKeys)
        .order('performed_at', { ascending: false })
        .limit(50);
      for (const p of priors ?? []) {
        if (!priorLiftsByKey[p.lift_key]) priorLiftsByKey[p.lift_key] = [];
        priorLiftsByKey[p.lift_key].push({
          liftKey: p.lift_key,
          weight: p.weight,
          weightUnit: p.weight_unit,
          performedAt: p.performed_at,
        });
      }
    }

    // Try server-side validation first (thresholds not in client binary)
    let integrity = validateWorkout(liftEntries, priorLiftsByKey); // fallback
    try {
      const signedHeaders = await getSignedHeaders(profile.id, 'validate-workout');
      const { data: serverValidation, error: svError } = await supabase.functions.invoke(
        'validate-workout',
        {
          body: { user_id: profile.id, lifts: liftEntries },
          headers: signedHeaders,
        },
      );
      if (!svError && serverValidation?.ok) {
        // Use server result for hard rejects — server thresholds are authoritative
        if (serverValidation.overall === 'hard_reject') {
          const firstHard = serverValidation.results?.find((r: any) => r.verdict === 'hard_reject');
          const rejectMsg = firstHard?.flags?.[0]?.message ?? 'This workout contains entries that exceed known human limits.';
          throw new Error(rejectMsg);
        }
      }
    } catch (svErr: any) {
      // If it's our hard-reject, re-throw; otherwise fall back to local validation
      if (svErr.message?.includes('exceed') || svErr.message?.includes('limits')) throw svErr;
      log.warn('[AntiGaming] Server validation unavailable, using local fallback:', svErr.message);
    }

    if (integrity.overall === 'hard_reject') {
      // Find the first hard-rejected entry for the error message
      let rejectMsg = 'This workout contains entries that exceed known human limits.';
      for (const [, result] of integrity.results) {
        const hardFlag = result.flags.find(f => f.severity === 'hard');
        if (hardFlag) { rejectMsg = hardFlag.message; break; }
      }
      throw new Error(rejectMsg);
    }

    // Log soft flags to integrity_flags table (non-blocking)
    if (integrity.overall === 'soft_flag') {
      const flagRows: any[] = [];
      for (const [idx, result] of integrity.results) {
        for (const flag of result.flags) {
          flagRows.push({
            profile_id: profile.id,
            flag_code: flag.code,
            flag_message: flag.message,
            severity: flag.severity,
            verdict: result.verdict,
            lift_key: liftEntries[idx]?.liftKey,
            weight: liftEntries[idx]?.weight,
            reps: liftEntries[idx]?.reps,
          });
        }
      }
      if (flagRows.length > 0) {
        supabase.from('integrity_flags').insert(flagRows).then(({ error }) => {
          if (error) log.warn('Failed to log integrity flags:', error);
        });
      }
    }
  } catch (err: any) {
    // If it's our hard_reject, re-throw; otherwise don't block the save
    if (err.message?.includes('exceed') || err.message?.includes('limits')) throw err;
    log.warn('Anti-gaming check failed (non-blocking):', err);
  }

  // --- Insert workout row (with offline fallback) ---
  let workoutData: any;
  let savedOffline = false;

  try {
    const result = await supabase
      .from('workouts')
      .insert({
        user_id: profile.id,
        gym_id: profile.home_gym_id || null,
        performed_at: workoutDate.toISOString(),
        payload: workoutPayload,
      })
      .select()
      .single();

    if (result.error) {
      // Check if this looks like a network error (fetch failed, timeout, etc.)
      const msg = result.error.message?.toLowerCase() ?? '';
      const isNetworkError =
        msg.includes('fetch') ||
        msg.includes('network') ||
        msg.includes('timeout') ||
        msg.includes('failed to send') ||
        msg.includes('err_internet') ||
        result.error.code === 'PGRST301';

      if (isNetworkError) {
        throw new Error(`Network error: ${result.error.message}`);
      }

      log.error('Workout insert failed:', result.error);
      captureError(result.error, { context: 'workoutSave', userId: profile.id });
      throw new Error(`Failed to save workout: ${result.error.message}`);
    }

    workoutData = result.data;
  } catch (err: any) {
    // If it's a network/fetch error, queue offline
    const msg = err?.message?.toLowerCase() ?? '';
    const isNetworkError =
      msg.includes('network') ||
      msg.includes('fetch') ||
      msg.includes('timeout') ||
      msg.includes('err_internet') ||
      msg.includes('failed to send');

    if (isNetworkError) {
      log.info('Network unavailable — queuing workout offline');
      await offlineQueue.enqueue({
        userId: profile.id,
        gymId: profile.home_gym_id || null,
        performedAt: workoutDate.toISOString(),
        payload: workoutPayload,
      });
      savedOffline = true;
      setSyncStatus('pending', 1);
      trackEvent('workout_saved_offline', { userId: profile.id });

      const offlineMsg = 'Workout saved offline — will sync when connected.';
      if (Platform.OS === 'web') {
        alert(offlineMsg);
      } else {
        Alert.alert('Saved Offline', offlineMsg);
      }

      // Return a stub result — post-save steps (events, posts, badges) will
      // run when the queue syncs on reconnect.
      return {
        workoutId: `offline-${Date.now()}`,
        newMilestones: [],
        badgesUnlocked: [],
      };
    }

    // Re-throw non-network errors
    throw err;
  }

  log.info('Workout saved successfully:', workoutData.id);
  trackEvent('workout_saved', { workoutId: workoutData.id, exerciseCount: workoutPayload.exercises.length });
  const performedAt = workoutData.performed_at || workoutDate.toISOString();

  // --- Insert strength_events ---
  try {
    await insertStrengthEvents(profile, workoutPayload, performedAt, workoutData.id);
    if (refreshProfile) {
      try {
        await refreshProfile();
      } catch (e) {
        if (__DEV__) console.debug('[WorkoutSave] refreshProfile failed', e);
      }
    }
  } catch (e) {
    log.error('strength_events EXCEPTION', { message: (e as any)?.message || e, profileId: profile.id });
  }

  // --- PR detection: notify on new personal record for V1 lifts (non-blocking) ---
  (async () => {
    try {
      const V1_LIFT_KEYS = new Set(['back-squat', 'bench-press', 'deadlift', 'weighted-pull-up', 'dead-hang']);
      const { notifyFirstPR: _notifyFirstPR } = await import('@/lib/notificationTriggers');
      const unit = profile.units ?? 'lbs';

      for (const ex of workoutPayload.exercises) {
        const liftKey = normalizeToV1Lift(ex.name ?? '');
        if (!liftKey || !V1_LIFT_KEYS.has(liftKey)) continue;

        // Find max weight logged in this session for this exercise
        const sessionMax = ex.sets.reduce((best: number, s) => Math.max(best, Number(s.weight) || 0), 0);
        if (sessionMax <= 0) continue;

        // Query previous best (exclude current workout)
        const { data: prevBests } = await supabase
          .from('strength_events')
          .select('weight')
          .eq('profile_id', profile.id)
          .eq('lift_key', liftKey)
          .neq('workout_id', workoutData.id)
          .order('weight', { ascending: false })
          .limit(1);

        const prevBest = prevBests?.[0]?.weight ?? 0;
        // Don't fire on very first log (prevBest === 0)
        if (prevBest > 0 && sessionMax > prevBest) {
          _notifyFirstPR(profile.id, ex.name, sessionMax, unit).catch(() => {});
          log.info('[PR] New PR detected:', ex.name, sessionMax, unit, '>', prevBest);
        }
      }
    } catch (prErr) {
      log.warn('[PR] PR check failed (non-blocking):', prErr);
    }
  })();

  // --- Auto-submit to challenges ---
  try {
    await autoSubmitChallenges(
      { id: profile.id, gym_id: profile.gym_id },
      authUserId,
    );
  } catch (err) {
    if (__DEV__) console.debug('[ChallengeAutoSubmit] routine failed silently', err);
  }

  // --- Notify duel opponents ---
  try {
    const { data: activeDuels } = await supabase
      .from('duels')
      .select('id,challenger_id,challenged_id')
      .eq('status', 'active')
      .or(`challenger_id.eq.${profile.id},challenged_id.eq.${profile.id}`);

    if (activeDuels && activeDuels.length > 0) {
      const displayName = (profile as any).handle || (profile as any).display_name || 'Your opponent';
      for (const duel of activeDuels) {
        const opponentId = duel.challenger_id === profile.id ? duel.challenged_id : duel.challenger_id;
        notifyOpponentLoggedLift(opponentId, displayName, duel.id).catch(() => {});
      }
    }
  } catch (e) {
    log.warn('[DuelNotify] Failed to notify opponents:', e);
  }

  // --- Score recompute ---
  log.info('Recomputing scores for:', profile.id);
  await enqueueScoreJob(profile.id, 'workout_save');

  // Recompute duel_standard_strength_kg — prefer server-side, fallback to client
  try {
    let serverScoreOk = false;

    // Attempt 1: Edge function (server-side, tamper-proof)
    try {
      const recomputeSignedHeaders = await getSignedHeaders(profile.id, 'recompute-strength');
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'recompute-strength',
        {
          body: { user_id: profile.id },
          headers: recomputeSignedHeaders,
        },
      );
      if (fnError) throw fnError;
      if (fnData?.updated) {
        log.info('[StrengthScore] Server-side updated:', fnData.score);
        serverScoreOk = true;
      }
    } catch (edgeFnErr) {
      log.warn('[StrengthScore] Edge function failed, falling back to client-side:', edgeFnErr);
    }

    // Attempt 2: Postgres RPC (server-side alternative)
    if (!serverScoreOk) {
      try {
        const { data: rpcScore, error: rpcErr } = await supabase.rpc(
          'recompute_strength_score',
          { p_user_id: profile.id },
        );
        if (rpcErr) throw rpcErr;
        if (typeof rpcScore === 'number' && rpcScore > 0) {
          log.info('[StrengthScore] RPC updated:', rpcScore);
          serverScoreOk = true;
        }
      } catch (rpcErr) {
        log.warn('[StrengthScore] RPC failed, falling back to client-side:', rpcErr);
      }
    }

    // Attempt 3: Client-side fallback (existing logic)
    // SECURITY NOTE: Client-side scoring is a fallback only.
    // The server-side compute-score edge function is the authoritative scorer.
    // This fallback exists for offline/edge-function-down scenarios.
    // Scores computed here will be overwritten on next server-side recompute.
    if (!serverScoreOk) {
      const bw = Number(profile.bodyweight) || 0;
      const { data: allW } = await supabase.from('workouts').select('payload').eq('user_id', profile.id).order('performed_at', { ascending: false }).limit(100);
      const bestByLift: Record<string, number> = {};
      const unmatchedNames: string[] = [];
      (allW || []).forEach((w: any) => {
        const pl = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
        (pl?.exercises ?? []).forEach((ex: any) => {
          const lk = normalizeToV1Lift(ex.name || '');
          if (!lk) {
            if (ex.name && !unmatchedNames.includes(ex.name)) unmatchedNames.push(ex.name);
            return;
          }
          (ex.sets ?? []).forEach((s: any) => {
            let wt = Number(s.weight) || 0;
            const rp = Number(s.reps) || 0;
            if (lk === 'weighted-pull-up') {
              // Always compute e1RM on total weight (bodyweight + external load)
              if (wt > 0) {
                wt = wt + (bw > 0 ? bw : 0);
              } else {
                wt = bw > 0 ? bw : 150;
              }
            }
            if (wt <= 0 || rp < 1) return;
            const e1rm = calculateE1RM(wt, rp);
            if (!bestByLift[lk] || e1rm > bestByLift[lk]) bestByLift[lk] = e1rm;
          });
        });
      });

      if (unmatchedNames.length > 0) {
        log.warn('[StrengthScore] Unmatched exercise names:', unmatchedNames);
      }

      const liftValues = Object.values(bestByLift);
      const rawTotal = liftValues.reduce((s, v) => s + v, 0);
      const maxContrib = rawTotal * 0.25;
      const cappedTotal = liftValues.reduce((s, v) => s + Math.min(v, maxContrib), 0);
      const strengthScore = Math.round(cappedTotal);

      if (strengthScore > 0) {
        await supabase
          .from('profiles')
          .update({
            duel_standard_strength_kg: strengthScore,
            duel_standard_updated_at: new Date().toISOString(),
          })
          .eq('id', profile.id);
        log.info('[StrengthScore] Client-side updated:', strengthScore, 'lifts:', bestByLift);
      }
    }
  } catch (e) {
    log.warn('[StrengthScore] Failed:', e);
  }

  hapticsUtil.workoutSaved();

  // --- Check milestones ---
  let newMilestones: MilestoneDef[] = [];
  try {
    const milestoneResult = await checkMilestones(profile.id);
    newMilestones = milestoneResult.newMilestones;
  } catch (e) {
    log.warn('[Milestones] Check failed:', e);
  }

  // --- Evaluate badges (server-backed, comprehensive — covers all 25 badges) ---
  let badgesUnlocked: any[] = [];
  try {
    const newBadges = await evaluateBadges(profile.id);
    if (newBadges.length > 0) {
      badgesUnlocked = newBadges;
      log.info('New badges awarded:', newBadges);
      // Fire PR notification if first_pr just unlocked
      if (newBadges.includes('first_pr')) {
        const prEntry = entries.find(e => e.isPR);
        if (prEntry) {
          const { notifyFirstPR } = await import('@/lib/notificationTriggers');
          notifyFirstPR(
            profile.id,
            prEntry.exercise?.name ?? 'Lift',
            parseFloat(prEntry.weight) || 0,
            profile.units ?? 'lbs',
          ).catch(() => {});
        }
      }
    }
  } catch (e) {
    log.warn('[Badges] Evaluation failed:', e);
  }

  // --- Create PR posts ---
  for (const entry of entries) {
    if (entry.isPR) {
      hapticsUtil.prSubmitted();
      let videoUrl: string | null = null;
      if (entry.videoUri) {
        try {
          videoUrl = await uploadVideo(entry.videoUri, profile.id);
        } catch {
          log.warn('PR post video upload failed (non-blocking)');
        }
      }

      log.info('Creating PR post for', entry.exercise!.name);
      await supabase.from('posts').insert({
        user_id: profile.id,
        caption: `PR Recorded: ${entry.exercise!.name} - ${entry.weight}${profile.units} x ${entry.reps}`,
        post_type: 'pr',
        media_url: videoUrl,
        media_type: videoUrl ? 'video' : null,
        visibility: 'public',
        is_competition: false,
        gym_id: profile.home_gym_id || null,
      });
    }
  }

  // --- Workout summary post ---
  if (shouldAutoShare && workoutPayload.exercises.length > 0 && (() => {
    const totalVolume = workoutPayload.exercises.reduce(
      (sum: number, ex) =>
        sum + ex.sets.reduce((setSum: number, set) => setSum + set.weight * set.reps, 0),
      0,
    );
    return totalVolume > 0;
  })()) {
    const totalExercises = workoutPayload.exercises.length;
    const totalVolume = workoutPayload.exercises.reduce(
      (sum: number, ex) =>
        sum + ex.sets.reduce((setSum: number, set) => setSum + set.weight * set.reps, 0),
      0,
    );

    const exerciseNames = workoutPayload.exercises
      .slice(0, 3)
      .map((ex) => ex.name)
      .join(', ');

    const workoutSummary =
      totalExercises > 3 ? `${exerciseNames} +${totalExercises - 3} more` : exerciseNames;

    const workoutPost = `Workout Complete\n\n${workoutSummary}\n\nTotal Volume: ${totalVolume.toLocaleString()} ${profile.units}`;

    log.info('Creating workout summary post');
    await supabase.from('posts').insert({
      user_id: profile.id,
      caption: workoutPost,
      post_type: 'workout',
      visibility: 'public',
      is_competition: false,
      gym_id: profile.home_gym_id || null,
    });
  } else {
    log.info('Skipping workout post (auto-share disabled)');
  }

  return {
    workoutId: workoutData.id,
    newMilestones,
    badgesUnlocked,
  };

  } finally {
    isSaving = false;
  }
}
